import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Eye, Search, Trash2, Users } from 'lucide-react';
import { formatShortDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import { money } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
  type PageMeta,
} from '@/components/ui';
import { PERMISSIONS } from '@unifemga/shared';
import { useAuth } from '@/lib/auth';
import { DeleteMemberModal } from '@/components/DeleteMemberModal';
import { StatusBadge } from '@/components/StatusBadge';
import { KYC_STATUS, MEMBER_STATUS } from '@/lib/status';

interface MemberRow {
  id: string;
  membershipNumber: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  kycStatus: string;
  activeLoans: number;
  outstanding: string;
  inArrears: boolean;
  memberSince: string;
}

export default function AdminMembers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [deleting, setDeleting] = useState<MemberRow | null>(null);
  const [query, setQuery] = useState('');
  const [kycStatus, setKycStatus] = useState('');
  const [hasOverdue, setHasOverdue] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-members', page, query, kycStatus, hasOverdue],
    queryFn: () =>
      api.get<{ data: MemberRow[]; meta: PageMeta }>(
        `/members?pageSize=25&page=${page}${query ? `&q=${encodeURIComponent(query)}` : ''}${
          kycStatus ? `&kycStatus=${kycStatus}` : ''
        }${hasOverdue ? '&hasOverdue=true' : ''}`,
      ),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Members"
        description={data ? `${data.meta.total} members` : undefined}
      />

      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <Field label="Search" htmlFor="q" className="min-w-[220px] flex-1">
            <div className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-secondary"
              />
              <Input
                id="q"
                className="pl-9"
                placeholder="Name, membership number, email or phone"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              />
            </div>
          </Field>

          <Field label="KYC status" htmlFor="kyc" className="w-52">
            <Select id="kyc" value={kycStatus} onChange={(e) => { setKycStatus(e.target.value); setPage(1); }}>
              <option value="">All</option>
              {Object.entries(KYC_STATUS).map(([key, presentation]) => (
                <option key={key} value={key}>
                  {presentation.label}
                </option>
              ))}
            </Select>
          </Field>

          <label className="flex h-10 cursor-pointer items-center gap-2 text-sm font-semibold text-foreground">
            <input
              type="checkbox"
              checked={hasOverdue}
              onChange={(e) => { setHasOverdue(e.target.checked); setPage(1); }}
              className="h-4 w-4 accent-[hsl(var(--danger))]"
            />
            In arrears only
          </label>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data || data.data.length === 0 ? (
        <EmptyState icon={<Users size={20} />} title="No members match those filters" />
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Membership</Th>
                  <Th>Contact</Th>
                  <Th>Status</Th>
                  <Th>KYC</Th>
                  <Th numeric>Active loans</Th>
                  <Th numeric>Outstanding</Th>
                  <Th>Member since</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.data.map((member) => (
                  <tr
                    key={member.id}
                    onClick={() => navigate(`/admin/members/${member.id}`)}
                    className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                      member.inArrears ? 'bg-danger-soft/30' : ''
                    }`}
                  >
                    <Td className="font-medium">{member.name}</Td>
                    <Td className="numeric text-foreground-secondary">
                      {member.membershipNumber || (
                        <span className="text-xs italic text-foreground-secondary/70">
                          Not assigned
                        </span>
                      )}
                    </Td>
                    <Td>
                      <p className="text-sm text-foreground-secondary">{member.email}</p>
                      <p className="numeric text-xs text-foreground-secondary">{member.phone}</p>
                    </Td>
                    <Td>
                      <StatusBadge map={MEMBER_STATUS} status={member.status} />
                    </Td>
                    <Td>
                      <StatusBadge map={KYC_STATUS} status={member.kycStatus} />
                    </Td>
                    <Td numeric>{member.activeLoans}</Td>
                    <Td numeric className={member.inArrears ? 'font-bold text-danger' : ''}>
                      {money(member.outstanding)}
                    </Td>
                    <Td className="numeric text-foreground-secondary">
                      {formatShortDate(new Date(member.memberSince))}
                    </Td>
                    <Td>
                      <div
                        className="flex items-center justify-end gap-1"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Open ${member.name}`}
                          onClick={() => navigate(`/admin/members/${member.id}`)}
                        >
                          <Eye size={15} />
                        </Button>
                        {can(PERMISSIONS.MEMBERS_DELETE as never) && (
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={`Delete ${member.name}`}
                            className="text-danger hover:bg-danger-soft"
                            onClick={() => setDeleting(member)}
                          >
                            <Trash2 size={15} />
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </CardContent>
        </Card>
      )}

      {deleting && (
        <DeleteMemberModal
          memberId={deleting.id}
          name={deleting.name}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void queryClient.invalidateQueries({ queryKey: ['admin-members'] });
          }}
        />
      )}
    </div>
  );
}
